import React, { useState, useEffect } from 'react';
import { useNavigation } from '../navigation';
import DistributorHistoryTable from './Distributor-history-table';
import DistributorHistoryStats from './Distributor-history-stats';
import DistributorRequest from './Distrubutor-request';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};



const DistributorsHistory: React.FC = () => {
  const { navigateTo } = useNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [distributorsData, setDistributorsData] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [apiError, setApiError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [amountFilter, setAmountFilter] = useState('all');
  
  // Request stock functionality state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestingDistributor, setRequestingDistributor] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch distributors data on component mount
  useEffect(() => {
    fetchDistributorsData();
  }, []);

  // Trigger animation when component loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Fetch distributors data from API
  const fetchDistributorsData = async () => {
    try {
      setIsLoadingData(true);
      setApiError('');
      
      // Get auth token from localStorage using utility function
      const authToken = getAuthToken();
      
      if (!authToken) {
        setApiError('Authentication token not found. Please log in again.');
        setIsLoadingData(false);
        return;
      }
      
      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/distributors/', {
        method: 'GET',
        mode: 'cors', // Explicitly set CORS mode
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle different data structures
      if (Array.isArray(data)) {
        // Direct array response
        setDistributorsData(data);
      } else if (data && data.distributors && Array.isArray(data.distributors)) {
        // Object with distributors property
        setDistributorsData(data.distributors);
      } else if (data && data.data && Array.isArray(data.data)) {
        // Object with data property
        setDistributorsData(data.data);
      } else {
        setDistributorsData([]);
      }
    } catch (error: any) {
      // Check for different types of errors
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('preflight request') || error.message.includes('ERR_FAILED')) {
        setApiError(`
          CORS Error: Unable to connect to the distributors API.
          
          Possible solutions:
          1. Check if your server is running
          2. Verify CORS is properly configured on the server
          3. Ensure ngrok tunnel is active
          4. Try refreshing the page
          
          Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/distributors
        `);
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setApiError('Authentication failed. Please log in again.');
        // Don't auto-remove token - let user manually logout
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        setApiError('Access denied. You do not have permission to view distributors data.');
      } else {
        setApiError(`Failed to load distributors data: ${error.message}`);
      }
      
      // Fallback to sample data if API fails
      setDistributorsData(sampleDistributorsData);
    } finally {
      setIsLoadingData(false);
    }
  };


  // Handle request stock button click
  const handleRequestStock = (distributor: any) => {
    setRequestingDistributor(distributor);
    setShowRequestModal(true);
  };

  // Close request modal
  const closeRequestModal = () => {
    setShowRequestModal(false);
    setRequestingDistributor(null);
  };

  // Trigger load animation and fetch data
  useEffect(() => {
    setIsLoaded(true);
    fetchDistributorsData();
  }, []);

  // Sample distributors data (fallback)
  const sampleDistributorsData = [
    {
      id: 1,
      distributorName: 'MediCorp Pharmaceuticals',
      contactPerson: 'John Smith',
      phone: '+91 98765 43210',
      email: 'john@medicorp.com',
      address: '123 Medical Street, Mumbai',
      totalAmount: 12500.00,
      pendingAmount: 2500.00,
      paidAmount: 10000.00,
      totalMedicines: 150,
      status: 'partial',
      lastPurchaseDate: '2024-01-15',
      medications: [
        { name: 'Paracetamol 500mg', quantity: 100, price: 5.99 },
        { name: 'Amoxicillin 250mg', quantity: 50, price: 12.50 },
        { name: 'Omeprazole 20mg', quantity: 30, price: 18.75 }
      ]
    },
    {
      id: 2,
      distributorName: 'HealthFirst Supplies',
      contactPerson: 'Sarah Johnson',
      phone: '+91 87654 32109',
      email: 'sarah@healthfirst.com',
      address: '456 Health Avenue, Delhi',
      totalAmount: 8900.00,
      pendingAmount: 0.00,
      paidAmount: 8900.00,
      totalMedicines: 89,
      status: 'paid',
      lastPurchaseDate: '2024-01-12',
      medications: [
        { name: 'Aspirin 100mg', quantity: 200, price: 3.25 },
        { name: 'Ibuprofen 400mg', quantity: 150, price: 7.50 }
      ]
    },
    {
      id: 3,
      distributorName: 'PharmaPlus Distributors',
      contactPerson: 'Michael Brown',
      phone: '+91 76543 21098',
      email: 'michael@pharmaplus.com',
      address: '789 Pharma Road, Bangalore',
      totalAmount: 21000.00,
      pendingAmount: 21000.00,
      paidAmount: 0.00,
      totalMedicines: 200,
      status: 'pending',
      lastPurchaseDate: '2024-01-10',
      medications: [
        { name: 'Atorvastatin 20mg', quantity: 90, price: 22.50 },
        { name: 'Metoprolol 50mg', quantity: 120, price: 18.75 }
      ]
    },
    {
      id: 4,
      distributorName: 'Global Medical Supplies',
      contactPerson: 'Emily Davis',
      phone: '+91 65432 10987',
      email: 'emily@globalmed.com',
      address: '321 Global Plaza, Chennai',
      totalAmount: 6750.00,
      pendingAmount: 0.00,
      paidAmount: 6750.00,
      totalMedicines: 45,
      status: 'paid',
      lastPurchaseDate: '2024-01-08',
      medications: [
        { name: 'Diazepam 5mg', quantity: 50, price: 25.00 },
        { name: 'Lorazepam 1mg', quantity: 40, price: 18.75 }
      ]
    },
    {
      id: 5,
      distributorName: 'MediLife Distributors',
      contactPerson: 'Robert Wilson',
      phone: '+91 54321 09876',
      email: 'robert@medilife.com',
      address: '654 Life Street, Pune',
      totalAmount: 15000.00,
      pendingAmount: 7500.00,
      paidAmount: 7500.00,
      totalMedicines: 120,
      status: 'partial',
      lastPurchaseDate: '2024-01-05',
      medications: [
        { name: 'Simvastatin 20mg', quantity: 70, price: 19.99 },
        { name: 'Pravastatin 40mg', quantity: 50, price: 21.50 }
      ]
    }
  ];

  const filteredDistributors = distributorsData.filter(distributor => {
    // Add null/undefined checks to prevent runtime errors
    if (!distributor || typeof distributor !== 'object') {
      return false;
    }

    const name = distributor.name || distributor.distributor_name || distributor.company_name || '';
    const email = distributor.email || distributor.email_address || '';
    const phone = distributor.phone || distributor.phone_number || distributor.contact || '';
    const unsettledAmount = distributor.unsettled_amount || distributor.unsettledAmount || distributor.pending_amount || distributor.pendingAmount || 0;
    const settledAmount = distributor.settled_amount || distributor.settledAmount || distributor.paid_amount || distributor.paidAmount || 0;

    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      phone.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Determine status based on unsettled amount
    const distributorStatus = unsettledAmount === 0 ? 'paid' : 
                            settledAmount > 0 ? 'partial' : 'pending';
    const matchesStatus = statusFilter === 'all' || distributorStatus === statusFilter;
    
    const matchesAmount = amountFilter === 'all' || 
      (amountFilter === 'high' && unsettledAmount > 5000) ||
      (amountFilter === 'medium' && unsettledAmount > 1000 && unsettledAmount <= 5000) ||
      (amountFilter === 'low' && unsettledAmount > 0 && unsettledAmount <= 1000) ||
      (amountFilter === 'none' && unsettledAmount === 0);
    
    return matchesSearch && matchesStatus && matchesAmount;
  });


  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '0',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Enhanced Animated Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 40% 60%, rgba(245, 158, 11, 0.06) 0%, transparent 50%),
          radial-gradient(circle at 60% 30%, rgba(139, 92, 246, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 10% 70%, rgba(236, 72, 153, 0.04) 0%, transparent 50%)
        `,
        animation: 'backgroundFloat 25s ease-in-out infinite'
      }}></div>

      {/* Content Container */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        {/* Enhanced Modern Header */}
        <header style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(25px)',
          borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
          padding: '1.75rem 0',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          transform: isLoaded ? 'translateY(0)' : 'translateY(-20px)',
          opacity: isLoaded ? 1 : 0,
          transition: 'all 0.6s ease-out',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: isMobile ? '0 0.5rem' : '0 2rem',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'center' : 'center',
            gap: isMobile ? '1rem' : '0',
            width: '100%',
            position: 'relative',
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}>
            {/* Top Row: Back Button and Title (Mobile) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              position: isMobile ? 'relative' : 'static'
            }}>
              {/* Back Button - Far Left */}
              <button
                onClick={() => navigateTo('pharma-homepage')}
                style={{
                  background: 'linear-gradient(135deg, #1B5E20 0%, #0D4A14 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(27, 94, 32, 0.3)',
                  transition: 'all 0.3s ease',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: '0',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(27, 94, 32, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(27, 94, 32, 0.3)';
                }}
              >
                ←
              </button>

              {/* Center Heading - Absolutely Centered (Desktop) / Relative (Mobile) */}
              <div style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: isMobile ? '1.25rem' : '1.5rem',
                fontWeight: '700',
                color: '#1f2937',
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}>
                Distributors History
              </div>

              {/* Spacer for mobile to balance layout */}
              <div style={{
                width: isMobile ? '36px' : '0',
                flexShrink: 0
              }}></div>
            </div>

            {/* Search Bar - Full Width on Mobile, Right Side on Desktop */}
            <div style={{ 
              position: 'relative', 
              width: isMobile ? '100%' : 'auto',
              minWidth: isMobile ? '0' : '300px',
              maxWidth: isMobile ? '100%' : 'none',
              marginLeft: isMobile ? '0' : 'auto',
              boxSizing: 'border-box'
            }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{
                  position: 'absolute',
                  left: isMobile ? '1rem' : '1.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  zIndex: 1
                }}
              >
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search by distributor name, contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: isMobile ? '0.875rem 1rem 0.875rem 3rem' : '1rem 1rem 1rem 3.5rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  backgroundColor: 'white',
                  color: '#1f2937',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                  maxWidth: '100%'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}
              />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main style={{
          maxWidth: isMobile ? '100%' : '1400px',
          margin: '0 auto',
          padding: isMobile ? '1rem 0.5rem' : '2rem',
          transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
          opacity: isLoaded ? 1 : 0,
          transition: 'all 0.6s ease-out 0.2s',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Stats Overview */}
          <DistributorHistoryStats distributorsData={distributorsData} />

          {/* Filters Section */}
          <section style={{
            marginBottom: '2rem'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(25px)',
              borderRadius: '16px',
              padding: '1.5rem',
              border: '1px solid rgba(16, 185, 129, 0.1)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                color: '#1f2937',
                margin: '0 0 1rem 0'
              }}>
                Filters & Search
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                alignItems: 'end'
              }}>
                <div>
                  <label style={{
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    marginBottom: '0.5rem',
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Status Filter
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.875rem 2.5rem 0.875rem 1rem',
                        border: '2px solid rgba(226, 232, 240, 0.6)',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        backgroundColor: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
                        color: '#374151',
                        outline: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxSizing: 'border-box',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#10b981';
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 0.95) 100%)';
                        e.currentTarget.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.1), 0 4px 16px rgba(16, 185, 129, 0.15)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.02)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <option 
                        value="all" 
                        style={{ 
                          padding: '0.75rem 1rem', 
                          backgroundColor: 'white', 
                          color: '#374151',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: 'none',
                          outline: 'none'
                        }}
                      >
                        All Status
                      </option>
                      <option 
                        value="paid" 
                        style={{ 
                          padding: '0.75rem 1rem', 
                          backgroundColor: 'white', 
                          color: '#374151',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: 'none',
                          outline: 'none'
                        }}
                      >
                        Paid
                      </option>
                      <option 
                        value="partial" 
                        style={{ 
                          padding: '0.75rem 1rem', 
                          backgroundColor: statusFilter === 'partial' ? '#3b82f6' : 'white', 
                          color: statusFilter === 'partial' ? 'white' : '#374151',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: 'none',
                          outline: 'none'
                        }}
                      >
                        Partial
                      </option>
                      <option 
                        value="pending" 
                        style={{ 
                          padding: '0.75rem 1rem', 
                          backgroundColor: 'white', 
                          color: '#374151',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: 'none',
                          outline: 'none'
                        }}
                      >
                        Pending
                      </option>
                    </select>
                    
                    {/* Custom Dropdown Arrow */}
                    <div style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}>
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        style={{ color: '#6b7280' }}
                      >
                        <polyline points="6,9 12,15 18,9"/>
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    marginBottom: '0.5rem',
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Amount Filter
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={amountFilter}
                      onChange={(e) => setAmountFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.875rem 2.5rem 0.875rem 1rem',
                        border: '2px solid rgba(226, 232, 240, 0.6)',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        backgroundColor: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
                        color: '#374151',
                        outline: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxSizing: 'border-box',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 0.95) 100%)';
                        e.currentTarget.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1), 0 4px 16px rgba(59, 130, 246, 0.15)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.6)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.02)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <option 
                        value="all" 
                        style={{ 
                          padding: '0.75rem 1rem', 
                          backgroundColor: 'white', 
                          color: '#374151',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: 'none',
                          outline: 'none'
                        }}
                      >
                        All Amounts
                      </option>
                      <option 
                        value="high" 
                        style={{ 
                          padding: '0.75rem 1rem', 
                          backgroundColor: amountFilter === 'high' ? '#3b82f6' : 'white', 
                          color: amountFilter === 'high' ? 'white' : '#374151',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: 'none',
                          outline: 'none'
                        }}
                      >
                        High (&gt;₹5000)
                      </option>
                      <option 
                        value="medium" 
                        style={{ 
                          padding: '0.75rem 1rem', 
                          backgroundColor: amountFilter === 'medium' ? '#3b82f6' : 'white', 
                          color: amountFilter === 'medium' ? 'white' : '#374151',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: 'none',
                          outline: 'none'
                        }}
                      >
                        Medium (₹1000-5000)
                      </option>
                      <option 
                        value="low" 
                        style={{ 
                          padding: '0.75rem 1rem', 
                          backgroundColor: amountFilter === 'low' ? '#3b82f6' : 'white', 
                          color: amountFilter === 'low' ? 'white' : '#374151',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: 'none',
                          outline: 'none'
                        }}
                      >
                        Low (&lt;₹1000)
                      </option>
                      <option 
                        value="none" 
                        style={{ 
                          padding: '0.75rem 1rem', 
                          backgroundColor: amountFilter === 'none' ? '#3b82f6' : 'white', 
                          color: amountFilter === 'none' ? 'white' : '#374151',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: 'none',
                          outline: 'none'
                        }}
                      >
                        No Pending
                      </option>
                    </select>
                    
                    {/* Custom Dropdown Arrow */}
                    <div style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}>
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        style={{ color: '#6b7280' }}
                      >
                        <polyline points="6,9 12,15 18,9"/>
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    marginBottom: '0.5rem',
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Distributor name, contact..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      backgroundColor: 'white',
                      color: '#1f2937',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Loading State */}
          {isLoadingData && (
            <div style={{
              textAlign: 'center',
              padding: '4rem',
              color: '#6b7280'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #e5e7eb',
                borderTop: '4px solid #10b981',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1.5rem auto'
              }}></div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Loading Distributors...
              </h3>
              <p style={{
                fontSize: '1rem',
                color: '#6b7280'
              }}>
                Please wait while we fetch the data
              </p>
            </div>
          )}

          {/* Error State */}
          {apiError && !isLoadingData && (
            <div style={{
              textAlign: 'center',
              padding: '4rem',
              color: '#6b7280'
            }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '1.5rem', opacity: 0.5 }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                {apiError}
              </h3>
              <button
                onClick={fetchDistributorsData}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Distributors List */}
          {!isLoadingData && !apiError && (
            <section>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <h2 style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: 0
                }}>
                  Distributors ({filteredDistributors.length})
                </h2>
              </div>

              {/* Distributors Table */}
              <DistributorHistoryTable
                filteredDistributors={filteredDistributors}
                distributorsData={distributorsData}
                handleRequestStock={handleRequestStock}
              />

            {filteredDistributors.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '4rem',
                color: '#6b7280'
              }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '1.5rem', opacity: 0.5 }}>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  No distributors found
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#6b7280'
                }}>
                  Try adjusting your search criteria
                </p>
              </div>
            )}
          </section>
        )}
        </main>
      </div>

      {/* Request Stock Modal */}
      <DistributorRequest
        showModal={showRequestModal}
        requestingDistributor={requestingDistributor}
        onClose={closeRequestModal}
      />

      <style>{`
        input::placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-webkit-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-moz-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input:-ms-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes backgroundFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default DistributorsHistory;
